import { SingleProductRowPlaceholder } from "@faire/retailer-visitor-shared/components/SearchProducts/SingleProductRowPlaceholder/index";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { SearchPageEfficientListLoader } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageResults/__internal__/SearchPageEfficientListLoader/SearchPageEfficientListLoader";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isPrerender } from "@faire/web--source/common/user-agent/isPrerender";
import { ResponsiveValues } from "@faire/web--source/slate/Theme/ResponsiveValues";
import { useResponsiveProp } from "@faire/web--source/ui/hooks/useResponsiveProp";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import React, { useMemo } from "react";

const VISIBILITY_THRESHOLD = -400;

const ROWS_PER_BATCH = 2;

const ITEMS_PER_ROW_XL_DESKTOP = 6;

export const MaybeRSCEfficientListLoader = ({
  requestId,
  itemsPerRowResponsive,
  displayProducts,
  componentToInsert,
  renderProduct,
}: {
  requestId: string | undefined;
  itemsPerRowResponsive: ResponsiveValues<number>;
  displayProducts: (ILeanProductTile | undefined)[];
  componentToInsert?: {
    component: React.ReactNode;
    groupIndex: number;
  };
  renderProduct: (
    productTile: ILeanProductTile | undefined,
    index?: number
  ) => React.JSX.Element;
}) => {
  const isSSR = useIsSSR();

  const RSCDesktopAndAboveItemsPerRow = isSSR
    ? ITEMS_PER_ROW_XL_DESKTOP
    : itemsPerRowResponsive.desktopAndAbove;

  const batchSize: ResponsiveValues<number> = useMemo(() => {
    return {
      mobileAndAbove: itemsPerRowResponsive.mobileAndAbove * ROWS_PER_BATCH,
      tabletAndAbove: itemsPerRowResponsive.tabletAndAbove
        ? itemsPerRowResponsive.tabletAndAbove * ROWS_PER_BATCH
        : undefined,
      desktopAndAbove: RSCDesktopAndAboveItemsPerRow
        ? RSCDesktopAndAboveItemsPerRow * ROWS_PER_BATCH
        : undefined,
      xLargeAndAbove: itemsPerRowResponsive.xLargeAndAbove
        ? itemsPerRowResponsive.xLargeAndAbove * ROWS_PER_BATCH
        : undefined,
      xxLargeDesktop: itemsPerRowResponsive.xxLargeDesktop
        ? itemsPerRowResponsive.xxLargeDesktop * ROWS_PER_BATCH
        : undefined,
    };
  }, [
    RSCDesktopAndAboveItemsPerRow,
    itemsPerRowResponsive.mobileAndAbove,
    itemsPerRowResponsive.tabletAndAbove,
    itemsPerRowResponsive.xLargeAndAbove,
    itemsPerRowResponsive.xxLargeDesktop,
  ]);

  const placeholderFunction = React.useCallback(
    () => (
      <SingleProductRowPlaceholder
        itemsPerRowResponsive={itemsPerRowResponsive}
      />
    ),
    [itemsPerRowResponsive]
  );

  return (
    <SearchPageEfficientListLoader
      data={displayProducts}
      batchSize={useResponsiveProp(batchSize)}
      renderComponent={renderProduct}
      forceRender={isPrerender()}
      // Remember to update the placeholder if the tile changes
      placeholderFn={placeholderFunction}
      threshold={VISIBILITY_THRESHOLD}
      hashKey={`${requestId}-${displayProducts?.length}`}
      componentToInsert={componentToInsert}
    />
  );
};
